/**
 * Arrow Object
 * @author David <david.prenninger@gmx.de>
 *
 * This file provides all helpers related to Arrow Object Visualization.
 */
import * as THREE from 'three';
import TextSprite from '@seregpie/three.text-sprite';
import {
  ARROW_X,
  ARROW_Y,
  ARROW_CHILD_ANGLE,
  ARROW_CHILD_RADIUS,
  ARROW_HEIGHT,
  ARROW_RAISED_WIDTH,
  ARROW_RAISED_HEIGHT,
  ARROW_OFFSET,
} from '../../constants';
import { degreeToMatrix, degreeToMatrixX } from './geometry';


const getFrontOrigin = (dimensions, side) => {
  const origin = new THREE.Vector3(0, 0, 0);
  const directionMatrix = degreeToMatrix(0);
  dimensions.forEach((dimension, index) => {
    if (index < side) {
      const frontVector = new THREE.Vector3(0, 0, dimension.y);
      frontVector.applyMatrix4(directionMatrix);
      origin.add(frontVector);
      directionMatrix.multiply(degreeToMatrix(dimension.childAngle));
    }
  });
  return { origin, directionMatrix };
};

const generateArrow = (origin, directionMatrix, length, metric) => {
  const arrowGroup = new THREE.Object3D();
  const vector = new THREE.Vector3(0, 0, 1).applyMatrix4(directionMatrix);
  const helper1 = new THREE.ArrowHelper(
    vector.normalize(),
    origin,
    length,
    0x00a2ff,
  );
  arrowGroup.add(helper1);
  const helper2 = new THREE.ArrowHelper(
    vector.negate(),
    origin.sub(vector.clone().multiplyScalar(length)),
    length,
    0x00a2ff,
  );
  arrowGroup.add(helper2);

  let valueInMetric;
  if (metric) {
    valueInMetric = Math.floor(length * 500);
  } else {
    valueInMetric = Number(((length * 500) / 25.4).toFixed(1));
  }

  const sprite = new TextSprite({
    text: `${valueInMetric}${metric ? 'mm' : '"'}`,
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: Math.max(length / 10, 0.04),
    fillStyle: '#9C0020',
    strokeStyle: '#ffffff',
    strokeWidth: 0.1,
  });
  const halfArrowVector = new THREE.Vector3(0, 0, length / 2)
    .applyMatrix4(
      directionMatrix,
    );
  const textPosition = origin.clone().sub(halfArrowVector);
  sprite.position.set(
    textPosition.x,
    textPosition.y,
    textPosition.z,
  );
  arrowGroup.add(sprite);
  return arrowGroup;
};

const generateArrowX = (origin, directionMatrix, arrow, tableDimensions, metric) => {
  origin.add(
    new THREE.Vector3(0, 0, tableDimensions[arrow.side].y / 2)
      .applyMatrix4(directionMatrix),
  );
  const outArrow = generateArrow(
    origin,
    directionMatrix.multiply(degreeToMatrix(90)),
    tableDimensions[arrow.side].x,
    metric,
  );
  outArrow.position.y = tableDimensions[0].z
    + (tableDimensions[0].raisedHeight ? tableDimensions[0].raisedHeight : 0) + 0.1;
  return outArrow;
};

const generateArrowY = (origin, directionMatrix, arrow, tableDimensions, metric) => {
  origin.add(
    new THREE.Vector3(0, 0, -0.3)
      .applyMatrix4(directionMatrix.clone().multiply(degreeToMatrix(90))),
  );
  const outArrow = generateArrow(
    origin,
    directionMatrix,
    tableDimensions[arrow.side].y,
    metric,
  );
  outArrow.position.y = (tableDimensions[0].z
    + (tableDimensions[0].raisedHeight ? tableDimensions[0].raisedHeight : 0)
  ) / 2;
  return outArrow;
};

const generateArrowHeight = (tableDimensions, metric) => {
  const outGroup = new THREE.Object3D();
  const origin = new THREE.Vector3(0, 0, 0);
  const directionMatrix = degreeToMatrix(0);
  tableDimensions.forEach((dimension) => {
    const frontVector = new THREE.Vector3(0, 0, dimension.y);
    frontVector.applyMatrix4(directionMatrix);
    origin.add(frontVector);

    // draw arrow
    const directionToTop = degreeToMatrixX(270);
    const outArrow = generateArrow(
      origin.clone().add(new THREE.Vector3(0, 0, 0.05).applyMatrix4(directionMatrix)),
      directionToTop,
      tableDimensions[0].z,
      metric,
    );
    outGroup.add(outArrow);
    directionMatrix.multiply(degreeToMatrix(dimension.childAngle));
  });
  return outGroup;
};

const generateArrowRaisedHeight = (tableDimensions, metric) => {
  const outGroup = new THREE.Object3D();
  const origin = new THREE.Vector3(0, tableDimensions[0].z, 0);
  const directionMatrix = degreeToMatrix(0);
  tableDimensions.forEach((dimension) => {
    const frontVector = new THREE.Vector3(0, 0, dimension.y);
    frontVector.applyMatrix4(directionMatrix);
    origin.add(frontVector);

    // draw arrow
    const directionToTop = degreeToMatrixX(270);
    const outArrow = generateArrow(
      origin.clone().add(new THREE.Vector3(0, 0, 0.05).applyMatrix4(directionMatrix)),
      directionToTop,
      (tableDimensions[0].raisedHeight ? tableDimensions[0].raisedHeight : 0),
      metric,
    );
    outGroup.add(outArrow);
    directionMatrix.multiply(degreeToMatrix(dimension.childAngle));
  });
  return outGroup;
};

const generateArrowRaisedWidth = (tableDimensions, metric) => {
  const outGroup = new THREE.Object3D();
  const origin = new THREE.Vector3(0, 0, 0);
  const directionMatrix = degreeToMatrix(0);
  tableDimensions.forEach((dimension) => {
    // draw arrow
    const tmpOrigin = origin.clone().add(
      new THREE.Vector3(0, 0, dimension.y / 2)
        .applyMatrix4(directionMatrix),
    );
    const outArrow = generateArrow(
      tmpOrigin,
      directionMatrix.clone().multiply(degreeToMatrix(90)),
      (tableDimensions[0].raisedWidth ? tableDimensions[0].raisedWidth : 0),
      metric,
    );
    outArrow.position.y = tableDimensions[0].z
      + (tableDimensions[0].raisedHeight ? tableDimensions[0].raisedHeight : 0) + 0.1;

    outGroup.add(outArrow);

    const frontVector = new THREE.Vector3(0, 0, dimension.y);
    frontVector.applyMatrix4(directionMatrix);
    origin.add(frontVector);
    directionMatrix.multiply(degreeToMatrix(dimension.childAngle));
  });
  return outGroup;
};

export default {
  // {side: 0, type: [constant], color: [HEX] }
  generate: (arrow, tableDimensions, offset, metric) => {
    if (arrow && tableDimensions) {
      if (typeof arrow.side !== 'undefined') {
        const { origin, directionMatrix } = getFrontOrigin(tableDimensions, arrow.side);
        switch (arrow.type) {
          case ARROW_X:
            return generateArrowX(origin, directionMatrix, arrow, tableDimensions, metric);
          case ARROW_Y:
            return generateArrowY(origin, directionMatrix, arrow, tableDimensions, metric);
          case ARROW_CHILD_ANGLE:
            break;
          case ARROW_CHILD_RADIUS:
            break;
          default:
            return undefined;
        }
      }

      let group;
      let yOffsetVector;
      let xOffsetVector;
      switch (arrow.type) {
        case ARROW_HEIGHT:
          return generateArrowHeight(tableDimensions, metric);
        case ARROW_RAISED_WIDTH:
          return generateArrowRaisedWidth(tableDimensions, metric);
        case ARROW_RAISED_HEIGHT:
          return generateArrowRaisedHeight(tableDimensions, metric);
        case ARROW_OFFSET:
          group = new THREE.Object3D();
          yOffsetVector = new THREE.Vector3(
            0,
            tableDimensions[0].z
              + (tableDimensions[0].raisedHeight ? tableDimensions[0].raisedHeight : 0) + 0.01,
            offset.y,
          );
          xOffsetVector = new THREE.Vector3(
            offset.x,
            tableDimensions[0].z
              + (tableDimensions[0].raisedHeight ? tableDimensions[0].raisedHeight : 0) + 0.01,
            0,
          );
          group.add(generateArrow(xOffsetVector, degreeToMatrix(0), offset.y, metric));
          group.add(generateArrow(yOffsetVector, degreeToMatrix(90), offset.x, metric));
          return group;
        default:
          return undefined;
      }
    }
    return undefined;
  },
  // Generates Center Distance Arrows
  generateCenterArrows: (offset, dimensions, objectSections, standApproval) => {
    const group = new THREE.Object3D();
    const linePoint = new THREE.Vector3(
      offset.x,
      dimensions[0].z + (dimensions[0].raisedHeight ? dimensions[0].raisedHeight : 0) + 0.1,
      offset.y,
    );
    const lineDirectionMatrix = degreeToMatrix(0);
    // Iterate over all objectSections
    objectSections.forEach((object, index) => {
      // Draw Arrow here
      const helpArrowFront = generateArrow(
        linePoint.clone(),
        lineDirectionMatrix.clone().multiply(degreeToMatrix(-90)),
        standApproval[index].stand.distanceFront,
        true,
      );
      group.add(helpArrowFront);
      const helpArrowBack = generateArrow(
        linePoint.clone(),
        lineDirectionMatrix.clone().multiply(degreeToMatrix(90)),
        standApproval[index].stand.distanceBack,
        true,
      );
      group.add(helpArrowBack);

      if (object.connector) {
        if (object.connector.size > 0) {
          if (object.connector.angle === 90 || object.connector.angle === 45) {
            let objectSize = new THREE.Vector3(0, 0, object.connector.size / 1000);
            if (!object.stand) {
              objectSize = objectSize.clone()
                .applyMatrix4(lineDirectionMatrix)
                .add(objectSize.clone().applyMatrix4(
                  lineDirectionMatrix.clone().multiply(degreeToMatrix(object.connector.angle)),
                ));
            } else {
              objectSize = objectSize.applyMatrix4(lineDirectionMatrix);
            }
            linePoint.add(objectSize);
          } else {
            const connectorVector = new THREE.Vector3(0, 0, object.connector.size / 500);
            connectorVector.applyMatrix4(lineDirectionMatrix);
            linePoint.add(connectorVector);
          }
        }
        lineDirectionMatrix.multiply(degreeToMatrix(object.connector.angle));
      }
      if (object.glass) {
        const glassVector = new THREE.Vector3(0, 0, object.glass.width);
        glassVector.applyMatrix4(lineDirectionMatrix);
        linePoint.add(glassVector);
      }
    });
    return group;
  },
};
